import { flow } from 'fp-ts/function';
import { at, getOr, isEmpty, map } from 'lodash/fp';
import { Fragment, useCallback } from 'react';
import { Fields, WrappedFieldInputProps, WrappedFieldMetaProps } from 'redux-form';
import { setEarliestAcceptableEndDateTime } from '@mews-commander/form';
import { ResponsiveTwoColumns, useTimeService } from '@mews-commander/ui';
import { IntervalTimeFieldsProps } from '../../types';
import { updateNilEventHandlersWithNoop } from '../../utils';
import { validation } from '../../validation';
import { HourSelectField } from './HourSelectField';
import TimeStringPickerField from './TimeStringPickerField';

const DEFAULT_AUTOFILL_HOUR_DIFFERENCE = 1;

export const HourIntervalFields = ({
    autofillHourDifference = DEFAULT_AUTOFILL_HOUR_DIFFERENCE,
    minHourDifference = 0,
    autoCorrect = true,
    correctionTimePolicy = setEarliestAcceptableEndDateTime,
    inline = false,
    withTimePicker = false,
    isTheSameDay,
    ...props
}: IntervalTimeFieldsProps) => {
    const { maxHourDifference, startProps, endProps, fieldNames, constraints, parse } = props;
    const timeService = useTimeService();
    const getTimeService = useCallback(() => timeService, [timeService]);

    const {
        isValidShortString,
        fromShortString,
        addHours,
        isTimeLessOrEqual,
        isTimeGreaterOrEqual,
        isTimeGreater,
        getShortString,
    } = timeService;

    const autoCorrectHourRange = ({
        startDateInput,
        endDateInput,
        startDateMeta,
        endDateMeta,
    }: {
        startDateInput: WrappedFieldInputProps;
        endDateInput: WrappedFieldInputProps;
        startDateMeta: WrappedFieldMetaProps;
        endDateMeta: WrappedFieldMetaProps;
    }) => {
        const dirty = startDateMeta.dirty || endDateMeta.dirty;

        if (dirty && isValidShortString(startDateInput.value) && isValidShortString(endDateInput.value)) {
            const startDateTime = fromShortString(startDateInput.value);
            const endDateTime = fromShortString(endDateInput.value);

            const isWithinMinHourDifference =
                minHourDifference != null
                    ? isTimeLessOrEqual(addHours(startDateTime, minHourDifference), endDateTime)
                    : true;
            const isWithinMaxHourDifference = maxHourDifference
                ? isTimeGreaterOrEqual(addHours(startDateTime, maxHourDifference), endDateTime)
                : true;

            if (isTimeGreater(startDateTime, endDateTime) || !isWithinMinHourDifference || !isWithinMaxHourDifference) {
                const { newStartDateTime = null, newEndDateTime = null } = correctionTimePolicy({
                    startDateTime,
                    getTimeService,
                    autofillHourDifference,
                    minHourDifference,
                    maxHourDifference,
                });
                if (newStartDateTime !== startDateTime) {
                    startDateInput.onChange(newStartDateTime && getShortString(newStartDateTime));
                }
                if (newEndDateTime !== endDateTime) {
                    endDateInput.onChange(newEndDateTime && getShortString(newEndDateTime));
                }
            }
        }
    };

    const renderDateFields = ({ names, ...fields }: $TSFixMe) => {
        const [startFieldProps, endFieldProps] = map(updateNilEventHandlersWithNoop, at(names, fields));
        if (autoCorrect && isTheSameDay) {
            autoCorrectHourRange({
                startDateInput: startFieldProps.input,
                endDateInput: endFieldProps.input,
                startDateMeta: startFieldProps.meta,
                endDateMeta: endFieldProps.meta,
            });
        }

        const FieldComponent = withTimePicker ? TimeStringPickerField : HourSelectField;

        const startDateTimeField = (
            <FieldComponent name={startFieldProps.input.name} {...startFieldProps} {...startProps} />
        );
        const endDateTimeField = <FieldComponent name={endFieldProps.input.name} {...endFieldProps} {...endProps} />;

        const Component = inline ? ResponsiveTwoColumns : Fragment;

        return (
            <Component>
                {startDateTimeField}
                {endDateTimeField}
            </Component>
        );
    };

    const validate = (value: string, allValues: { [name: string]: string }, validateProps: $TSFixMe, name: string) => {
        if (!isEmpty(allValues)) {
            const validationMessage = flow(validation(constraints), getOr(null, name))(allValues, validateProps);
            return validationMessage;
        }
    };

    const createValidation = () => {
        const [startFieldName, endFieldName] = fieldNames;
        return {
            [startFieldName]: validate,
            [endFieldName]: validate,
        };
    };

    return <Fields component={renderDateFields} names={fieldNames} validate={createValidation()} parse={parse} />;
};
